import { events, type EventsChannel } from 'aws-amplify/data';
import { IRemoteChat, MessageDoc, OutgoingMessage } from './apis/chat';
import { extractGameIdentifier, getAuthToken, getNamespace } from '../utils/amplify-config';

type SubscriptionPayload = {
    id: string;
    type: 'data';
    event: MessageDoc;
};

/**
 * Amplify Events implementation of IRemoteChat
 * Uses AWS AppSync Events for real-time chat functionality
 */
class AmplifyChat implements IRemoteChat {
    private channels: Map<string, EventsChannel> = new Map();
    private subscriptions: Map<string, any> = new Map();

    /**
     * Get or create an EventsChannel for a specific channelId/phase combination
     */
    private async getOrCreateChannel(
        channelId: string
    ): Promise<EventsChannel> {
        if (this.channels.has(channelId)) {
            return this.channels.get(channelId)!;
        }

        const gameIdentifier = extractGameIdentifier(channelId);
        const channelName = `${getNamespace()}/chat/${gameIdentifier}`;
        console.log('Connecting to channel:', channelName);

        const channel = await events.connect(channelName, { authToken: getAuthToken() });

        this.channels.set(channelId, channel);
        return channel;
    }

    /**
     * Subscribe to message updates for a specific channel
     * Note: This subscribes to ALL phases for the channel
     * If you need phase-specific subscriptions, modify the interface to accept phase parameter
     */
    onMessageSnapshot(channelId: string, cb: (doc: MessageDoc) => void) {
        this.unsubscribe(channelId);

        const promise = this.getOrCreateChannel(channelId)
            .then((channel) => {
                // If cancelled while connecting, don't subscribe
                if (!this.subscriptions.has(channelId)) return;

                const subscription = channel.subscribe({
                    next: ({ event: message }: SubscriptionPayload) => {
                        cb(message);
                    },
                    error: (error: Error) => {
                        console.error(`Error in chat channel ${channelId}:`, error);
                    },
                });

                this.subscriptions.set(channelId, subscription);
            })
            .catch((error) => {
                console.error(`Failed to create channel ${channelId}:`, error);
                this.subscriptions.delete(channelId);
            });

        // Store promise to indicate "connecting"
        this.subscriptions.set(channelId, promise);

        return () => this.unsubscribe(channelId);
    }

    private unsubscribe(key: string) {
        if (this.subscriptions.has(key)) {
            const sub = this.subscriptions.get(key);
            // If it's a subscription object
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
            // If it's a pending promise, we just remove it from the map.
            // The .then() block checks the map before subscribing, so it will respect this removal.
            this.subscriptions.delete(key);
        }
    }

    /**
     * Send a message to a specific channel and phase
     */
    async sendMessage(
        channelId: string,
        phase: string,
        message: OutgoingMessage
    ): Promise<void> {
        const messageId = this.generateMessageId();
        const channel = await this.getOrCreateChannel(channelId);

        const messageDoc: MessageDoc = {
            ...message,
            messageId,
            channelId,
            phase,
            viewers: [message.sender],
        };

        await channel.publish(messageDoc, { authToken: getAuthToken() });
    }

    /**
     * Load existing messages for a channel from MongoDB
     */
    async getMessages(channelId: string): Promise<MessageDoc[]> {
        try {
            const apiUrl = process.env.API_ENDPOINT;
            const encodedChannelId = encodeURIComponent(channelId);
            const url = `${apiUrl}/chat/${encodedChannelId}/messages`;

            console.log('💬 [Chat] Loading messages from:', url);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Failed to load messages: ${response.statusText}`);
            }

            const messages = await response.json();
            console.log(`💬 [Chat] Loaded ${messages.length} messages for channel ${channelId}`);

            return messages;
        } catch (error) {
            console.error('Failed to load messages:', error);
            return [];
        }
    }

    /**
     * Mark a message as read by a viewer
     * Publishes a read receipt event to a separate channel
     */
    async markMessageAsRead(
        channelId: string,
        messageId: string,
        viewer: string
    ): Promise<void> {
        try {
            console.log('👁️ [Chat] Marking message as read:', { channelId, messageId, viewer });

            // Publish read receipt to the MAIN chat channel (not /receipts)
            const channel = await this.getOrCreateChannel(channelId);

            await channel.publish(
                {
                    type: 'read_receipt',
                    messageId,
                    viewer,
                    timestamp: Date.now(),
                },
                { authToken: getAuthToken() }
            );

            console.log('✅ [Chat] Read receipt published:', { messageId, viewer });

            // Note: The backend will update MongoDB, but we don't wait for a response
            // The message will be marked as read on next page load from MongoDB
        } catch (error) {
            console.error('❌ [Chat] Failed to mark message as read:', error);
            // Don't throw - read receipts are not critical
        }
    }

    /**
     * Generate a unique message ID
     */
    generateMessageId(): string {
        return typeof window !== 'undefined' && window?.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : Date.now()
                .toString(36)
                .concat('-')
                .concat(Math.ceil(Math.random() * Date.now()).toString(36));
    }

    /**
     * Clean up all channels and subscriptions
     * Call this when the component/app unmounts
     */
    cleanup(): void {
        // Unsubscribe from all subscriptions
        this.subscriptions.forEach((sub) => {
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
        });
        this.subscriptions.clear();

        this.channels.forEach((channel) => {
            channel.close();
        });
        this.channels.clear();
    }
}

// Export singleton instance
export const chat: IRemoteChat = new AmplifyChat();
